import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import Decimal from 'decimal.js';
import { defaultBillingDayOfWeek } from '~/constants';
import { DiscountModel, DiscountType, OneTimeInStagesModel, serviceBillingModel, serviceModel, StagesTerms, StagesValueType } from '~/models';
import { makeAwaitingApprovalStatus, makeOneTimeAutomaticStatus } from '~/models/service';
import { ServiceSource } from '~/models/service/model';
import { makeBillUpfrontState, makeOnApprovalBilling, makeOnDemandBilling, makeOneTimeManualBilling, makeOneTimeInStagesBilling, makeOngoingBilling, makeRepeatableManualBilling, oneTimeInStages } from '~/models/service-billing';
import { PreapprovalPeriodType, RecurrencePeriodTypes } from '~/models/service-billing/model';
import { makeFixedCost, makeRangeCost, makeVariableCost } from '~/models/service-cost';
import { ServiceCostType } from '~/models/service-cost/model';
import { PracticeManagementServiceIntegrationModel, QboServiceIntegrationModel, ServiceIntegrationsModel, XeroServiceIntegrationModel } from '~/models/ServiceIntegrations';
import { utils } from '~/utils';
export function isRecurringPeriod(recurrencePeriod) {
    return recurrencePeriod !== 'onetime' && recurrencePeriod !== 'inStages' && recurrencePeriod !== 'onDemand';
}
export const defaultDiscountType = DiscountType.Percent;
export function getPriceAfterDiscount(data) {
    const { price, minPrice, pricing, discount } = data;
    if (!price || !discount.amount || !+discount.amount) return '';
    const priceWithDiscount = utils.formatDollarAmount(utils.applyDiscount(new Decimal(+price), DiscountModel.make(discount.type, +discount.amount)));
    if (pricing === 'range') {
        const minPriceWithDiscount = utils.formatDollarAmount(utils.applyDiscount(new Decimal(+minPrice), DiscountModel.make(discount.type, +discount.amount)));
        if (!minPrice) {
            return "Up to ".concat(priceWithDiscount);
        }
        return "".concat(minPriceWithDiscount, "-").concat(priceWithDiscount);
    }
    return priceWithDiscount;
}
export const outputToCost = (param)=>{
    let { output, originalService } = param;
    const { pricing, minPrice, recurrencePeriod } = output;
    const discount = (()=>{
        var _originalService_cost_discount;
        const amount = output.discount.amount && +output.discount.amount;
        if (!amount) return null;
        const expiration = isRecurringPeriod(recurrencePeriod) ? originalService === null || originalService === void 0 ? void 0 : (_originalService_cost_discount = originalService.cost.discount) === null || _originalService_cost_discount === void 0 ? void 0 : _originalService_cost_discount.expiration() : undefined;
        return output.discount.type === DiscountType.Percent ? DiscountModel.makePercent(amount, expiration) : DiscountModel.makeValue(amount, expiration);
    })();
    const cost = (()=>{
        const price = +output.price;
        const priceIncrease = originalService && serviceModel.priceIncrease(originalService);
        switch(pricing){
            case ServiceCostType.Fixed:
                return makeFixedCost(price, {
                    discount,
                    priceIncrease
                });
            case ServiceCostType.Range:
                return makeRangeCost(price, {
                    minPrice: minPrice ? +minPrice : null,
                    discount,
                    priceIncrease
                });
            default:
                return makeVariableCost(price, {
                    discount,
                    unitName: pricing === 'unit' && output.unitName || null,
                    unitCap: output.unitCap ? +output.unitCap : 0,
                    priceIncrease
                });
        }
    })();
    return cost;
};
export function outputToService(param) {
    let { output, isAgreement, originalService, generateUniqueId, applyOutputToChanges, agreementData } = param;
    const { serviceName, description, trigger, recurrencePeriod, bundle } = output;
    const cost = outputToCost({
        output,
        originalService
    });
    const billing = (()=>{
        if (recurrencePeriod === 'onetime') {
            const oneTimeBillingMap = {
                approval: ()=>makeOnApprovalBilling(),
                oneTimeInStages: ()=>makeOneTimeInStagesBilling(output.oneTimeInStagesBilling || OneTimeInStagesModel.make({
                        valueType: StagesValueType.Percent,
                        terms: StagesTerms.AutoFirst,
                        stages: [
                            {
                                stageNumber: 1,
                                value: new Decimal(50)
                            },
                            {
                                stageNumber: 2,
                                value: new Decimal(50)
                            }
                        ]
                    })),
                manualFixed: ()=>makeOneTimeManualBilling(),
                manualVariable: ()=>makeOneTimeManualBilling()
            };
            return oneTimeBillingMap[trigger]();
        } else if (recurrencePeriod === 'inStages') {
            return makeOneTimeInStagesBilling(output.oneTimeInStagesBilling || OneTimeInStagesModel.make({
                valueType: StagesValueType.Percent,
                terms: StagesTerms.AutoFirst,
                stages: [
                    {
                        stageNumber: 1,
                        value: new Decimal(50)
                    },
                    {
                        stageNumber: 2,
                        value: new Decimal(50)
                    }
                ]
            }));
        } else if (recurrencePeriod === 'onDemand') {
            const maxCharges = originalService && serviceModel.maxCharges(originalService);
            return makeOnDemandBilling(output.preapprovalPeriod, {
                maxCharges
            });
        } else {
            const billingDayOfWeek = (()=>{
                if (isAgreement) {
                    return originalService ? serviceModel.billingDayOfWeek(originalService) || defaultBillingDayOfWeek : defaultBillingDayOfWeek;
                } else {
                    return originalService ? serviceModel.billingDayOfWeek(originalService) || defaultBillingDayOfWeek : defaultBillingDayOfWeek;
                }
            })();
            const maxCharges = originalService && serviceModel.maxCharges(originalService);
            return trigger === 'ongoing' ? makeOngoingBilling(recurrencePeriod, {
                billingDayOfMonth: originalService ? serviceModel.billingDayOfMonth(originalService) : null,
                billingDayOfWeek,
                isBilledUpfront: originalService ? serviceModel.isBilledUpfront(originalService) : false,
                billUpfrontState: makeBillUpfrontState(!!(originalService && serviceModel.isSkipBillingOnAcceptance(originalService))),
                isPaused: originalService && serviceModel.isPaused(originalService),
                isProrated: originalService && serviceModel.isProrated(originalService),
                maxCharges,
                isAwaitingApproval: !originalService || serviceModel.isAwaitingApproval(originalService.status)
            }) : makeRepeatableManualBilling(recurrencePeriod, {
                maxCharges
            });
        }
    })();
    if (applyOutputToChanges && originalService) {
        const originalNote = serviceModel.note(originalService);
        const withChanges = serviceModel.setChanges(originalService, {
            name: serviceName,
            description,
            cost,
            billing,
            bundle: bundle && bundle.length >= 0 ? {
                services: bundle
            } : null
        });
        return originalNote ? serviceModel.setNote(withChanges, originalNote) : withChanges;
    } else {
        const qboIntegratedId = output.serviceQboMapping.type === 'unmapped' ? undefined : output.serviceQboMapping.mappedItem.id;
        const qboItemType = output.serviceQboMapping.type === 'unmapped' ? undefined : output.serviceQboMapping.mappedItem.type;
        const xeroIntegratedId = output.serviceXeroMapping.type === 'unmapped' ? undefined : output.serviceXeroMapping.mappedItem.id;
        const getStatus = ()=>{
            if (originalService) {
                return trigger === 'ongoing' ? originalService.status : makeOneTimeAutomaticStatus(originalService);
            }
            return makeAwaitingApprovalStatus();
        };
        return serviceModel.makeService(_object_spread_props(_object_spread({}, originalService || {}), {
            serviceTemplateId: originalService ? originalService.serviceTemplateId : generateUniqueId('service-template'),
            status: getStatus(),
            name: serviceName.trim(),
            description,
            source: originalService ? originalService.source : agreementData ? agreementData.serviceSource() : ServiceSource.Draft,
            cost,
            billing,
            bundle: bundle && bundle.length >= 0 ? {
                services: bundle
            } : undefined,
            integrations: originalService ? originalService.integrations.setKarbonIntegratedIds(output.karbonMapping).setFinancialCentsIntegratedIds(output.financialCentsMapping).setClientHubIntegratedIds(output.clientHubMapping).setKeeperIntegratedIds(output.keeperMapping).setQboIntegratedId(qboIntegratedId).setQboIntegratedType(qboItemType).setXeroIntegratedId(xeroIntegratedId) : output.financialCentsMapping.length || output.karbonMapping.length || output.clientHubMapping.length || output.keeperMapping.length || qboIntegratedId || xeroIntegratedId ? new ServiceIntegrationsModel({
                karbon: PracticeManagementServiceIntegrationModel.makeKarbon({
                    integratedIds: output.karbonMapping
                }),
                clientHub: PracticeManagementServiceIntegrationModel.makeClientHub({
                    integratedIds: output.clientHubMapping
                }),
                financialCents: PracticeManagementServiceIntegrationModel.makeFinancialCents({
                    integratedIds: output.financialCentsMapping
                }),
                keeper: PracticeManagementServiceIntegrationModel.makeKeeper({
                    integratedIds: output.keeperMapping
                }),
                qbo: new QboServiceIntegrationModel(qboIntegratedId || null, null, qboItemType),
                xero: new XeroServiceIntegrationModel(xeroIntegratedId || null)
            }) : ServiceIntegrationsModel.fromDTO(undefined)
        }));
    }
}
export function serviceToInput(param) {
    let { service, qboItems, xeroItems } = param;
    var _serviceBillingModel_preapprovalPeriod;
    var _service_bundle;
    const { name, description, billing, cost } = service;
    return {
        serviceName: name,
        description,
        recurrencePeriod: (()=>{
            const handleOneTime = ()=>'onetime';
            const handleRecurring = (billing)=>billing.recurrencePeriod;
            return serviceBillingModel.accept(billing, {
                visitOnApproval: handleOneTime,
                visitOneTimeManual: handleOneTime,
                visitOneTimeInStages: ()=>'inStages',
                visitOngoing: handleRecurring,
                visitRepeatableManual: handleRecurring,
                visitOnDemand: ()=>'onDemand'
            });
        })(),
        pricing: (()=>{
            switch(cost.pricingType){
                case ServiceCostType.Fixed:
                case ServiceCostType.Range:
                    return cost.pricingType;
                default:
                    return cost.unitName ? 'unit' : 'hour';
            }
        })(),
        price: (()=>{
            switch(cost.pricingType){
                case ServiceCostType.Fixed:
                case ServiceCostType.Variable:
                    return cost.price.toString();
                case ServiceCostType.Range:
                default:
                    return cost.maxPrice.toString();
            }
        })(),
        minPrice: (()=>{
            if (cost.pricingType !== 'range') return '';
            return cost.minPrice != null ? cost.minPrice.toString() : '';
        })(),
        unitName: cost.pricingType === 'variable' && cost.unitName || '',
        discount: {
            type: cost.discount ? cost.discount.type() : defaultDiscountType,
            amount: cost.discount ? cost.discount.amount().toString() : ''
        },
        unitCap: cost.pricingType === 'variable' && cost.unitCap != null && cost.unitCap.toString() || '',
        trigger: (()=>{
            const handleManual = ()=>{
                if (cost.pricingType === 'fixed') {
                    return 'manualFixed';
                } else {
                    return 'manualVariable';
                }
            };
            return serviceBillingModel.accept(billing, {
                visitOnApproval: ()=>'approval',
                visitOngoing: ()=>'ongoing',
                visitRepeatableManual: handleManual,
                visitOneTimeManual: handleManual,
                visitOneTimeInStages: handleManual,
                visitOnDemand: ()=>'onDemand'
            });
        })(),
        serviceQboMapping: getDefaultServiceMapping(service.integrations.qboIntegratedId(), qboItems || []),
        serviceXeroMapping: getDefaultServiceMapping(service.integrations.xeroIntegratedId(), xeroItems || []),
        karbonMapping: service.integrations.karbonIntegratedIds(),
        financialCentsMapping: service.integrations.financialCentsIntegratedIds(),
        clientHubMapping: service.integrations.clientHubIntegratedIds(),
        keeperMapping: service.integrations.keeperIntegratedIds(),
        preapprovalPeriod: (_serviceBillingModel_preapprovalPeriod = serviceBillingModel.preapprovalPeriod(billing)) !== null && _serviceBillingModel_preapprovalPeriod !== void 0 ? _serviceBillingModel_preapprovalPeriod : PreapprovalPeriodType.None,
        oneTimeInStagesBilling: oneTimeInStages(service.billing),
        bundle: (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services
    };
}
export function getDefaultServiceMapping(integratedId, items) {
    const mappedItem = items.find((item)=>item.id === integratedId);
    return integratedId && mappedItem ? {
        type: 'mapped',
        mappedItem
    } : {
        type: 'unmapped'
    };
}
export function getOccurrenceDisabledData(serviceOrigin, service) {
    const editingApprovedService = !!service && !serviceModel.isAwaitingApproval(service.status);
    const editingServiceFromAgreement = editingApprovedService && serviceOrigin === 'agreement';
    return {
        disabled: editingServiceFromAgreement,
        tooltip: editingServiceFromAgreement ? 'Billing occurrences cannot be modified; to modify an occurrence, create a new service and add it to the agreement.' : ''
    };
}
export function isNameFieldDisabled(service, serviceOrigin) {
    return !!service && serviceOrigin === 'agreement' && !serviceModel.isAwaitingApproval(service.status);
}
export const getPreapprovedSuffix = (recurrencePeriod)=>{
    switch(recurrencePeriod){
        case RecurrencePeriodTypes.Weekly:
            return '/week';
        case RecurrencePeriodTypes.BiWeekly:
            return '/two weeks';
        case RecurrencePeriodTypes.Monthly:
            return '/month';
        case RecurrencePeriodTypes.Quarterly:
            return '/quarter';
        case RecurrencePeriodTypes.Yearly:
            return '/year';
        case 'onetime':
        case 'inStages':
        case 'onDemand':
            return '';
    }
};
export const quillToText = (deltaStr)=>{
    try {
        const delta = JSON.parse(deltaStr);
        return delta.value.ops.map((op)=>op.insert || '').join('');
    } catch (e) {
        return deltaStr;
    }
};
